import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignStaffLevel,
  CreatePricingLevel,
  CreateServiceVariant,
  PricingLevel,
  ResolvePriceQuery,
  ResolvedPrice,
  ServiceLevelPrice,
  ServicePricingMatrix,
  ServiceVariant,
  SetServiceLevelPrice,
  StaffLevel,
  UpdatePricingLevel,
  UpdateServiceVariant,
} from '@salonomy/types';
import { api, expectOk, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS9 phase 1 — service pricing variants + per-staff pricing levels (brief 09 §1). TanStack Query
 * over the generated client, mirroring data/services.ts / data/policy.ts: unwrap()/expectOk() on the
 * typed client, @salonomy/types for every shape, never hand-rolled fetch and never client-side money
 * math — the price a booking surface shows is the one the API RESOLVES (GET /v1/pricing/resolve), so
 * shown == charged (the brief-07 honesty rule, extended to pricing).
 *
 * RBAC (mirror of the API): variant/level reads are open to any authenticated booking surface; writes
 * (create/update variant, level CRUD, staff-level assignment, level-price override) are manager-only.
 * The UI gates the editor to managers so a 403 is never the first feedback a user gets.
 */

// ── Service variants (nested under a service) ────────────────────────────────────

export function useServiceVariants(serviceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.serviceVariants(serviceId ?? ''),
    enabled: Boolean(serviceId) && enabled,
    queryFn: async (): Promise<ServiceVariant[]> =>
      unwrap(await api.GET('/v1/services/{id}/variants', { params: { path: { id: serviceId! } } })),
  });
}

export function useCreateServiceVariant(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateServiceVariant): Promise<ServiceVariant> =>
      unwrap(
        await api.POST('/v1/services/{id}/variants', {
          params: { path: { id: serviceId } },
          body: body as never,
        }),
      ),
    onSuccess: () => invalidateServicePricing(qc, serviceId),
  });
}

export function useUpdateServiceVariant(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      variantId: string;
      body: UpdateServiceVariant;
    }): Promise<ServiceVariant> =>
      unwrap(
        await api.PATCH('/v1/services/{id}/variants/{variantId}', {
          params: { path: { id: serviceId, variantId: input.variantId } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => invalidateServicePricing(qc, serviceId),
  });
}

export function useDeleteServiceVariant(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (variantId: string): Promise<void> =>
      expectOk(
        await api.DELETE('/v1/services/{id}/variants/{variantId}', {
          params: { path: { id: serviceId, variantId } },
        }),
      ),
    onSuccess: () => invalidateServicePricing(qc, serviceId),
  });
}

// ── Pricing levels (salon-wide: junior / senior / director) ───────────────────────

export function usePricingLevels() {
  return useQuery({
    queryKey: qk.pricingLevels,
    queryFn: async (): Promise<PricingLevel[]> => unwrap(await api.GET('/v1/pricing/levels')),
  });
}

export function useCreatePricingLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePricingLevel): Promise<PricingLevel> =>
      unwrap(await api.POST('/v1/pricing/levels', { body: body as never })),
    onSuccess: () => invalidateAllPricing(qc),
  });
}

export function useUpdatePricingLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      levelId: string;
      body: UpdatePricingLevel;
    }): Promise<PricingLevel> =>
      unwrap(
        await api.PATCH('/v1/pricing/levels/{levelId}', {
          params: { path: { levelId: input.levelId } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => invalidateAllPricing(qc),
  });
}

export function useDeletePricingLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (levelId: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/pricing/levels/{levelId}', { params: { path: { levelId } } })),
    onSuccess: () => invalidateAllPricing(qc),
  });
}

// ── Staff ↔ level assignment (the rate a staff member charges at) ─────────────────

/**
 * Assign a staff member to a pricing level (or clear it with `levelId: null` to charge the base).
 * A reassignment shifts every resolved price for that staff member, so the whole pricing subtree is
 * invalidated — no resolved price may go stale.
 */
export function useAssignStaffLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AssignStaffLevel): Promise<StaffLevel> =>
      unwrap(await api.PUT('/v1/pricing/staff-level', { body: body as never })),
    onSuccess: () => invalidateAllPricing(qc),
  });
}

// ── Per-(service[, variant], level) absolute price override ────────────────────────

/** Set an absolute price for a (service[, variant], level) combination — it wins over the bps level. */
export function useSetServiceLevelPrice(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetServiceLevelPrice): Promise<ServiceLevelPrice> =>
      unwrap(
        await api.PUT('/v1/services/{id}/level-prices', {
          params: { path: { id: serviceId } },
          body: body as never,
        }),
      ),
    onSuccess: () => invalidateServicePricing(qc, serviceId),
  });
}

/** Clear an absolute override — the combination reverts to the level's bps adjustment over base. */
export function useDeleteServiceLevelPrice(serviceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (overrideId: string): Promise<void> =>
      expectOk(
        await api.DELETE('/v1/services/{id}/level-prices/{overrideId}', {
          params: { path: { id: serviceId, overrideId } },
        }),
      ),
    onSuccess: () => invalidateServicePricing(qc, serviceId),
  });
}

// ── The variant × level matrix for a service (the editor grid) ─────────────────────

/**
 * The full pricing matrix for one service (variants, the salon's levels, the absolute overrides set on
 * combinations) plus the derived "from £X". The services editor renders this as a grid; the resolved
 * cell price is shown deterministically (never recomputed on the client).
 */
export function useServicePricingMatrix(serviceId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.pricingMatrix(serviceId ?? ''),
    enabled: Boolean(serviceId) && enabled,
    queryFn: async (): Promise<ServicePricingMatrix> =>
      unwrap(
        await api.GET('/v1/services/{id}/pricing-matrix', {
          params: { path: { id: serviceId! } },
        }),
      ),
  });
}

// ── Deterministic price resolution (shown before commitment) ───────────────────────

/**
 * Resolve the concrete price for a (service, variant?, staff?) combination. This is the ONLY source of
 * the price a booking surface shows before commitment — the UI never derives it from the matrix or the
 * level bps locally. Gated on a serviceId so a half-filled booking form doesn't fire the query.
 */
export function useResolvedPrice(query: Partial<ResolvePriceQuery>, enabled = true) {
  return useQuery({
    queryKey: qk.resolvedPrice(query.serviceId ?? '', query.variantId, query.staffId),
    enabled: Boolean(query.serviceId) && enabled,
    queryFn: async (): Promise<ResolvedPrice> =>
      unwrap(
        await api.GET('/v1/pricing/resolve', {
          params: {
            query: {
              serviceId: query.serviceId!,
              variantId: query.variantId,
              staffId: query.staffId,
            },
          },
        }),
      ),
  });
}

// ── Invalidation helpers ───────────────────────────────────────────────────────────

/** A change scoped to one service touches its variants, matrix, and every resolved price for it. */
function invalidateServicePricing(qc: ReturnType<typeof useQueryClient>, serviceId: string): void {
  void qc.invalidateQueries({ queryKey: qk.serviceVariants(serviceId) });
  void qc.invalidateQueries({ queryKey: qk.pricingMatrix(serviceId) });
  void qc.invalidateQueries({ queryKey: ['pricing', 'resolve'] });
  // `priceFrom` ("from £X") is derived from the matrix, so the services list may now differ.
  void qc.invalidateQueries({ queryKey: qk.services });
}

/** A salon-wide change (a level, a staff assignment) can shift any service's resolved prices. */
function invalidateAllPricing(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: qk.pricingLevels });
  void qc.invalidateQueries({ queryKey: ['pricing', 'matrix'] });
  void qc.invalidateQueries({ queryKey: ['pricing', 'resolve'] });
  void qc.invalidateQueries({ queryKey: qk.services });
}
